import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Building2,
  Users,
  BarChart3,
  Settings,
  Shield,
  Globe,
  Database,
  Activity,
  LogOut,
  User,
  ArrowLeft,
  Crown
} from 'lucide-react';
import axios from 'axios';

const MasterAdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [platformStats, setPlatformStats] = useState({});
  const [departments, setDepartments] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  
  const { user, logout, API } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPlatformData();
  }, []);

  const fetchPlatformData = async () => {
    try {
      setLoading(true);
      
      // Fetch platform statistics
      const statsResponse = await axios.get(`${API}/master/platform-stats`);
      setPlatformStats(statsResponse.data);
      
      // Fetch all departments
      const deptResponse = await axios.get(`${API}/master/departments`);
      setDepartments(deptResponse.data);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching platform data:', error);
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading Master Admin Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="glass border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Crown className="w-8 h-8 text-yellow-600" />
                <div>
                  <h1 className="text-xl font-bold text-slate-800">ReqTrak Platform</h1>
                  <p className="text-xs text-slate-600">Master Administration</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-slate-500">Master Owner</p>
                <p className="font-medium text-slate-800">{user?.full_name}</p>
              </div>
              
              <div className="h-6 w-px bg-gray-300" />
              
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-slate-600" />
                <span className="text-sm font-medium text-slate-700">{user?.email}</span>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="flex items-center gap-2 text-red-600 hover:text-red-700"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-2">Master Admin Dashboard</h2>
          <p className="text-slate-600">Platform-wide administration and management</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="departments" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Departments
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="system" className="flex items-center gap-2">
              <Database className="w-4 h-4" />
              System
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Departments</CardTitle>
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{platformStats.department_count || 0}</div>
                  <p className="text-xs text-muted-foreground">Active police departments</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{platformStats.user_count || 0}</div>
                  <p className="text-xs text-muted-foreground">All users across platform</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Requests</CardTitle>
                  <Activity className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{platformStats.request_count || 0}</div>
                  <p className="text-xs text-muted-foreground">Public records requests</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">System Status</CardTitle>
                  <Shield className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">Online</div>
                  <p className="text-xs text-muted-foreground">All systems operational</p>
                </CardContent>
              </Card>
            </div>

            <Alert>
              <Crown className="h-4 w-4" />
              <AlertDescription>
                <strong>Master Owner Access:</strong> You have full administrative control over the ReqTrak platform. 
                You can manage all departments, users, and system settings across the entire platform.
              </AlertDescription>
            </Alert>
          </TabsContent>

          {/* Departments Tab */}
          <TabsContent value="departments" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>All Departments</CardTitle>
                <CardDescription>Manage all police departments on the platform</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {departments.map((dept) => (
                    <div key={dept.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <Building2 className="w-8 h-8 text-blue-600" />
                        <div>
                          <h4 className="font-semibold">{dept.name}</h4>
                          <p className="text-sm text-slate-600">
                            {dept.user_count || 0} users • {dept.request_count || 0} requests
                          </p>
                          <p className="text-xs text-slate-500">
                            Owner: {dept.owner_name} ({dept.owner_email})
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="capitalize">
                          {dept.subscription_status || 'active'}
                        </Badge>
                        <Button variant="outline" size="sm">
                          Manage
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* User Statistics */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    User Overview
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Total Users</span>
                    <Badge variant="outline">{platformStats.user_count || 0}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Admins</span>
                    <Badge variant="outline" className="bg-red-50 text-red-700">
                      {departments.reduce((acc, dept) => acc + (dept.admin_count || 0), 0)}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Staff</span>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700">
                      {departments.reduce((acc, dept) => acc + (dept.staff_count || 0), 0)}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Citizens</span>
                    <Badge variant="outline" className="bg-green-50 text-green-700">
                      {platformStats.user_count - departments.reduce((acc, dept) => acc + (dept.admin_count || 0) + (dept.staff_count || 0), 0)}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Active Today</span>
                    <Badge variant="outline" className="bg-yellow-50 text-yellow-700">23</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* User Actions */}
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button className="w-full justify-start" variant="outline">
                    <Users className="w-4 h-4 mr-2" />
                    Create Master Admin
                  </Button>
                  <Button className="w-full justify-start" variant="outline">
                    <Shield className="w-4 h-4 mr-2" />
                    Reset User Password
                  </Button>
                  <Button className="w-full justify-start" variant="outline">
                    <Activity className="w-4 h-4 mr-2" />
                    Export User Data
                  </Button>
                  <Button className="w-full justify-start" variant="outline">
                    <Database className="w-4 h-4 mr-2" />
                    Bulk User Operations
                  </Button>
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent User Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <div>
                        <p className="font-medium">New user registered</p>
                        <p className="text-slate-500">john.doe@shakerpd.com</p>
                        <p className="text-xs text-slate-400">2 hours ago</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <div>
                        <p className="font-medium">Role updated</p>
                        <p className="text-slate-500">Admin → Staff</p>
                        <p className="text-xs text-slate-400">5 hours ago</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                      <div>
                        <p className="font-medium">Password reset</p>
                        <p className="text-slate-500">jane.smith@westmetro.gov</p>
                        <p className="text-xs text-slate-400">1 day ago</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* User Management Table */}
            <Card>
              <CardHeader>
                <CardTitle>All Platform Users</CardTitle>
                <CardDescription>Manage users across all departments</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Search and Filters */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex-1">
                    <input 
                      type="text" 
                      placeholder="Search users by name, email, or department..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <select className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option>All Departments</option>
                    {departments.map(dept => (
                      <option key={dept.id}>{dept.name}</option>
                    ))}
                  </select>
                  <select className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option>All Roles</option>
                    <option>Admin</option>
                    <option>Staff</option>
                    <option>User</option>
                  </select>
                </div>

                {/* Users Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left p-3 font-medium">User</th>
                        <th className="text-left p-3 font-medium">Department</th>
                        <th className="text-left p-3 font-medium">Role</th>
                        <th className="text-left p-3 font-medium">Status</th>
                        <th className="text-left p-3 font-medium">Last Active</th>
                        <th className="text-left p-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Sample users - in real app, this would come from API */}
                      <tr className="border-b hover:bg-slate-50">
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                              SA
                            </div>
                            <div>
                              <p className="font-medium">Sarah Martinez</p>
                              <p className="text-slate-500">request@shakerpd.com</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">Shaker Heights PD</td>
                        <td className="p-3">
                          <Badge variant="outline" className="bg-red-50 text-red-700">Admin</Badge>
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className="bg-green-50 text-green-700">Active</Badge>
                        </td>
                        <td className="p-3 text-slate-500">2 hours ago</td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline">Edit</Button>
                            <Button size="sm" variant="outline" className="text-red-600">Suspend</Button>
                          </div>
                        </td>
                      </tr>
                      
                      <tr className="border-b hover:bg-slate-50">
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                              JS
                            </div>
                            <div>
                              <p className="font-medium">John Staff</p>
                              <p className="text-slate-500">john.staff@shakerpd.com</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">Shaker Heights PD</td>
                        <td className="p-3">
                          <Badge variant="outline" className="bg-blue-50 text-blue-700">Staff</Badge>
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className="bg-green-50 text-green-700">Active</Badge>
                        </td>
                        <td className="p-3 text-slate-500">1 day ago</td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline">Edit</Button>
                            <Button size="sm" variant="outline" className="text-red-600">Suspend</Button>
                          </div>
                        </td>
                      </tr>
                      
                      <tr className="border-b hover:bg-slate-50">
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                              DB
                            </div>
                            <div>
                              <p className="font-medium">David Brown</p>
                              <p className="text-slate-500">duvallbrown@yahoo.com</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">Shaker Heights PD</td>
                        <td className="p-3">
                          <Badge variant="outline" className="bg-green-50 text-green-700">User</Badge>
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className="bg-green-50 text-green-700">Active</Badge>
                        </td>
                        <td className="p-3 text-slate-500">3 days ago</td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline">Edit</Button>
                            <Button size="sm" variant="outline" className="text-red-600">Suspend</Button>
                          </div>
                        </td>
                      </tr>
                      
                      <tr className="border-b hover:bg-slate-50">
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                              WO
                            </div>
                            <div>
                              <p className="font-medium">West Metro Owner</p>
                              <p className="text-slate-500">owner@westmetropd.gov</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">West Metro PD</td>
                        <td className="p-3">
                          <Badge variant="outline" className="bg-purple-50 text-purple-700">Owner</Badge>
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className="bg-green-50 text-green-700">Active</Badge>
                        </td>
                        <td className="p-3 text-slate-500">1 week ago</td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline">Edit</Button>
                            <Button size="sm" variant="outline" className="text-red-600">Suspend</Button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-6">
                  <p className="text-sm text-slate-500">Showing 1 to 4 of {platformStats.user_count || 0} users</p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled>Previous</Button>
                    <Button variant="outline" size="sm" className="bg-blue-600 text-white">1</Button>
                    <Button variant="outline" size="sm">2</Button>
                    <Button variant="outline" size="sm">3</Button>
                    <Button variant="outline" size="sm">Next</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* System Tab */}
          <TabsContent value="system" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {/* System Health */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    System Health
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">API Status</span>
                    <Badge className="bg-green-50 text-green-700 border-green-200">Online</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Database</span>
                    <Badge className="bg-green-50 text-green-700 border-green-200">Connected</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Email Service</span>
                    <Badge className="bg-green-50 text-green-700 border-green-200">Active</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">File Storage</span>
                    <Badge className="bg-green-50 text-green-700 border-green-200">Available</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">CDN</span>
                    <Badge className="bg-yellow-50 text-yellow-700 border-yellow-200">Slow</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Server Resources */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Server Resources
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>CPU Usage</span>
                      <span>68%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full" style={{width: '68%'}}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Memory Usage</span>
                      <span>42%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-green-600 h-2 rounded-full" style={{width: '42%'}}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Disk Usage</span>
                      <span>78%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-yellow-600 h-2 rounded-full" style={{width: '78%'}}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Network I/O</span>
                      <span>23%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-purple-600 h-2 rounded-full" style={{width: '23%'}}></div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* System Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="w-5 h-5" />
                    System Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Platform Version</span>
                    <span>v2.4.1</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Database Version</span>
                    <span>MongoDB 7.0</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Node.js Version</span>
                    <span>v20.11.0</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Last Backup</span>
                    <span>2 hours ago</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Uptime</span>
                    <span>14d 7h 23m</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Environment</span>
                    <span>Production</span>
                  </div>
                </CardContent>
              </Card>

              {/* Performance Metrics */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    Performance Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Avg Response Time</span>
                    <span className="text-green-600">245ms</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Requests/Hour</span>
                    <span>1,247</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Error Rate</span>
                    <span className="text-green-600">0.12%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Active Sessions</span>
                    <span>23</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Peak Users Today</span>
                    <span>67</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Cache Hit Rate</span>
                    <span className="text-green-600">94.3%</span>
                  </div>
                </CardContent>
              </Card>

              {/* Security Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Security Status
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">SSL Certificate</span>
                    <Badge className="bg-green-50 text-green-700 border-green-200">Valid</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Firewall</span>
                    <Badge className="bg-green-50 text-green-700 border-green-200">Active</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Failed Logins (24h)</span>
                    <Badge className="bg-yellow-50 text-yellow-700 border-yellow-200">7</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Blocked IPs</span>
                    <Badge className="bg-red-50 text-red-700 border-red-200">3</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Last Security Scan</span>
                    <span className="text-sm text-slate-600">6 hours ago</span>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle>System Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button className="w-full justify-start" variant="outline">
                    <Database className="w-4 h-4 mr-2" />
                    Run System Backup
                  </Button>
                  <Button className="w-full justify-start" variant="outline">
                    <Activity className="w-4 h-4 mr-2" />
                    Clear Cache
                  </Button>
                  <Button className="w-full justify-start" variant="outline">
                    <Shield className="w-4 h-4 mr-2" />
                    Security Scan
                  </Button>
                  <Button className="w-full justify-start" variant="outline" className="text-yellow-600">
                    <Settings className="w-4 h-4 mr-2" />
                    Restart Services
                  </Button>
                  <Button className="w-full justify-start" variant="outline" className="text-red-600">
                    <Database className="w-4 h-4 mr-2" />
                    Maintenance Mode
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* System Logs */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Recent System Logs
                </CardTitle>
                <CardDescription>Latest system events and notifications</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-green-800">System Backup Completed</p>
                        <span className="text-xs text-green-600">2 hours ago</span>
                      </div>
                      <p className="text-sm text-green-700">Automated daily backup completed successfully. Size: 2.3GB</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-blue-800">Database Connection Pool Optimized</p>
                        <span className="text-xs text-blue-600">4 hours ago</span>
                      </div>
                      <p className="text-sm text-blue-700">Connection pool resized to handle increased traffic load</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-yellow-800">High CPU Usage Detected</p>
                        <span className="text-xs text-yellow-600">6 hours ago</span>
                      </div>
                      <p className="text-sm text-yellow-700">CPU usage reached 85% during peak hours. Consider scaling.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-red-800">Failed Login Attempt</p>
                        <span className="text-xs text-red-600">8 hours ago</span>
                      </div>
                      <p className="text-sm text-red-700">Multiple failed login attempts from IP: 192.168.1.100</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="w-2 h-2 bg-slate-500 rounded-full mt-2"></div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-slate-800">System Update Available</p>
                        <span className="text-xs text-slate-600">1 day ago</span>
                      </div>
                      <p className="text-sm text-slate-700">ReqTrak v2.4.2 is available with security improvements</p>
                    </div>
                  </div>
                </div>

                {/* Log Controls */}
                <div className="flex items-center justify-between mt-6 pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <select className="px-3 py-1 border border-gray-300 rounded text-sm">
                      <option>All Logs</option>
                      <option>Errors Only</option>
                      <option>Warnings</option>
                      <option>Info</option>
                    </select>
                    <select className="px-3 py-1 border border-gray-300 rounded text-sm">
                      <option>Last 24 Hours</option>
                      <option>Last 7 Days</option>
                      <option>Last 30 Days</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline">Export Logs</Button>
                    <Button size="sm" variant="outline">Clear Old Logs</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Platform Configuration */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    Platform Configuration
                  </CardTitle>
                  <CardDescription>Global platform settings and branding</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Platform Name</label>
                    <input 
                      type="text" 
                      defaultValue="ReqTrak"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Platform Domain</label>
                    <input 
                      type="text" 
                      defaultValue="reqtrak.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Support Email</label>
                    <input 
                      type="email" 
                      defaultValue="support@reqtrak.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Platform Tagline</label>
                    <input 
                      type="text" 
                      defaultValue="Public Records Request Management System"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <Button className="w-full">
                    Update Platform Settings
                  </Button>
                </CardContent>
              </Card>

              {/* Security & Access */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Security & Access
                  </CardTitle>
                  <CardDescription>Platform-wide security settings</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Two-Factor Authentication</p>
                      <p className="text-sm text-slate-600">Require 2FA for all admin users</p>
                    </div>
                    <input type="checkbox" className="w-4 h-4" />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Email Verification</p>
                      <p className="text-sm text-slate-600">Require email verification for new users</p>
                    </div>
                    <input type="checkbox" defaultChecked className="w-4 h-4" />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Department Registration</p>
                      <p className="text-sm text-slate-600">Allow new department self-registration</p>
                    </div>
                    <input type="checkbox" defaultChecked className="w-4 h-4" />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Session Timeout (minutes)</label>
                    <input 
                      type="number" 
                      defaultValue="60"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <Button className="w-full">
                    Update Security Settings
                  </Button>
                </CardContent>
              </Card>

              {/* Email & Notifications */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    Email & Notifications
                  </CardTitle>
                  <CardDescription>Platform-wide email configuration</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">SMTP Server</label>
                    <input 
                      type="text" 
                      defaultValue="smtp.reqtrak.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">SMTP Port</label>
                      <input 
                        type="number" 
                        defaultValue="587"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Encryption</label>
                      <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option>TLS</option>
                        <option>SSL</option>
                        <option>None</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">From Email Address</label>
                    <input 
                      type="email" 
                      defaultValue="noreply@reqtrak.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Email Notifications</p>
                      <p className="text-sm text-slate-600">Send platform update notifications</p>
                    </div>
                    <input type="checkbox" defaultChecked className="w-4 h-4" />
                  </div>
                  
                  <Button className="w-full">
                    Update Email Settings
                  </Button>
                </CardContent>
              </Card>

              {/* Billing & Subscriptions */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Billing & Subscriptions
                  </CardTitle>
                  <CardDescription>Global billing configuration</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Trial Period (days)</label>
                    <input 
                      type="number" 
                      defaultValue="30"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Monthly Price ($)</label>
                    <input 
                      type="number" 
                      defaultValue="99"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Setup Fee ($)</label>
                    <input 
                      type="number" 
                      defaultValue="199"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Auto-billing</p>
                      <p className="text-sm text-slate-600">Automatically charge monthly subscriptions</p>
                    </div>
                    <input type="checkbox" defaultChecked className="w-4 h-4" />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Trial Extension</p>
                      <p className="text-sm text-slate-600">Allow Master Admin to extend trials</p>
                    </div>
                    <input type="checkbox" defaultChecked className="w-4 h-4" />
                  </div>
                  
                  <Button className="w-full">
                    Update Billing Settings
                  </Button>
                </CardContent>
              </Card>

              {/* System Maintenance */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="w-5 h-5" />
                    System Maintenance
                  </CardTitle>
                  <CardDescription>Platform maintenance and monitoring</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Maintenance Mode</label>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2">
                        <input type="radio" name="maintenance" value="off" defaultChecked />
                        <span>Off</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input type="radio" name="maintenance" value="scheduled" />
                        <span>Scheduled</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input type="radio" name="maintenance" value="emergency" />
                        <span>Emergency</span>
                      </label>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Backup Frequency</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option>Daily</option>
                      <option>Weekly</option>
                      <option>Monthly</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Log Retention (days)</label>
                    <input 
                      type="number" 
                      defaultValue="90"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1">
                      Run Backup
                    </Button>
                    <Button variant="outline" className="flex-1">
                      Clear Logs
                    </Button>
                  </div>
                  
                  <Button className="w-full">
                    Update System Settings
                  </Button>
                </CardContent>
              </Card>

              {/* API & Integrations */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    API & Integrations
                  </CardTitle>
                  <CardDescription>Third-party integrations and API settings</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Stripe API Key</label>
                    <input 
                      type="password" 
                      placeholder="sk_live_..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">OpenAI API Key</label>
                    <input 
                      type="password" 
                      placeholder="sk-..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Twilio SID</label>
                    <input 
                      type="password" 
                      placeholder="AC..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">API Rate Limiting</p>
                      <p className="text-sm text-slate-600">Enable API rate limiting</p>
                    </div>
                    <input type="checkbox" defaultChecked className="w-4 h-4" />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Rate Limit (requests/hour)</label>
                    <input 
                      type="number" 
                      defaultValue="1000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <Button className="w-full">
                    Update API Settings
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default MasterAdminDashboard;